import { escape } from "lodash";
import CustomGeneratedIdSettings from "~/models/customGeneratedIdSettings";
import Form, { IForm } from "../../../../formability/form_builder/models/form";
import FormElement from "../../../../formability/form_builder/models/formElements/formElement";
import WorkflowStep from "../../../../formability/form_builder/models/workflow/workflowStep";
import { DynamicReferenceType } from "../../../enums/dynamicReferenceType";
import { NumberHelper } from "../../../helpers/numberHelper";
import { IDynamicFieldsElementOptions, IFormElementDynamicFieldHandler } from "../../../interfaces/dynamicFieldsElementOptionsInterfaces";
import { IDynamicDataSectionConfig, IDynamicReferencedThing, IFormDynamicFieldSection } from "../../../interfaces/dynamicFieldsInterfaces";
import { IFormElementDynamicFieldsOptions } from "../../../interfaces/dynamicFieldsInterfaces/formElementDynamicFieldsInterfaces";
import IFormElementOptions from "../../../interfaces/formElementOptionsInterfaces";
import FormElementOptions from "../options/formElementOptions";
import DynamicFieldsHandler from "./dynamicFieldsHandler";
import {ModelType} from '~/enums/modelType';

export default class FormElementDynamicFieldsHandler extends DynamicFieldsHandler implements IFormElementDynamicFieldHandler {
  SECTIONS = ['formFieldData'];
  dynamicMessage: string;
  showDynamicMessage: boolean;
  _formFieldHeader: string = 'Form Field Data';
  allowElementContainerSelection: boolean = false;
  readonly includeRepeatables: boolean;
  readonly initialStepOnly: boolean;
  readonly indexableOnly: boolean;

  get formFieldHeader(): string  {
    return this._formFieldHeader;
  }
  form: IForm;
  workflowSteps?: WorkflowStep[];

  elementOptions: IDynamicFieldsElementOptions

  constructor(options: IFormElementDynamicFieldsOptions = {
    initialStepOnly: false,
    indexableOnly: false,
    elementOptions: new FormElementOptions(),
    includeRepeatables: true,
    formFieldHeader: 'Form Field Data',
  }) {
    super(options);
    this.includeRepeatables = options.includeRepeatables;
    this.initialStepOnly = options.initialStepOnly;
    this.indexableOnly = options.indexableOnly;
    this.elementOptions = options.elementOptions ?? new FormElementOptions();
    if (options.elementOptions?.getElements) this.elementOptions.getElements = options.elementOptions.getElements;
    if (options.elementOptions?.matcher) this.elementOptions.matcher = options.elementOptions.matcher;

    if (options.formFieldHeader) this._formFieldHeader = options.formFieldHeader;
    if (options.allowElementContainerSelection) this.allowElementContainerSelection = options.allowElementContainerSelection;
    this.form = options.form;
    this.workflowSteps = options.workflowSteps;
  }

  formElementSections(q: string, parent: FormElement | Form | CustomGeneratedIdSettings | null, restrictToParentContext: boolean): IFormDynamicFieldSection[] {
    const filters: ((e: FormElement) => boolean)[] = [
      this.defaultFilter(parent),
      (element) => this.elementOptions.matcher(element),
      (element) => element.referenceNameOrDefault.toLowerCase().match(q) !== null,
    ];
    if (this.indexableOnly) {
      filters.push(element => element.isIndexable);
    }

    const matcher: IFormElementOptions['matcher'] = (element) => {
      return filters.every(filter => filter(element));
    };

    let workflowSteps = this.workflowSteps ?? this.form.workflowStepsWithFormSections;
    if (this.initialStepOnly) {
      workflowSteps = workflowSteps.filter(workflowStep => workflowStep.isInitialStep);
    }
    return workflowSteps.map(ws => {
      let elements;
      //If parent is within repeatable can only reference within the repeatable
      if (restrictToParentContext && parent && 'repeatableAncestors' in parent && parent.repeatableAncestors && parent.repeatableAncestors.length > 0) {
        if (parent instanceof Form) {
          elements = this.elementOptions.getElements(ws.form_section, {includeRepeatables: this.includeRepeatables}).filter(matcher);
        } else if (parent && 'containerScope' in parent) {
          elements = this.elementOptions.getElements(parent.containerScope, {includeRepeatables: this.includeRepeatables}).filter(matcher);
        }
      } else {
        elements = this.elementOptions.getElements(ws.form_section, {includeRepeatables: this.includeRepeatables}).filter(matcher);
      }
      return this.buildFormElementSection(elements, ws, this.buildFormSectionDateOptions(ws));
    });
  }

  hasFormElementMatch(q: string, parent: FormElement, restrictToParentContext: boolean): boolean {
    return this.formElementSections(q, parent, restrictToParentContext).some(section => section.formElements.length > 0);
  }

  hasFieldDataMatch(q: string, parent: FormElement, restrictToParentContext: boolean): boolean {
    return this.hasFormElementMatch(q, parent, restrictToParentContext);
  }

  protected selectFormElement(element: FormElement, workflowStep: WorkflowStep, index?: number): IDynamicReferencedThing {
    const config: IDynamicReferencedThing = {
      type: DynamicReferenceType.FormElement,
      id: element.client_id,
      label: escape(element.referenceNameOrDefault),
      wsTitle: escape(workflowStep.titleOrDefault),
      pillColor: this.pillColor
    };

    if (NumberHelper.toIntOrNull(index) !== null) {
      config.index = index;
      config.label = config.label + "." + (index + 1);
    }

    return super.select(element, config)
  }

  protected buildFormElementSection(elements: FormElement[], workflowStep: WorkflowStep, dateOptions = [], nonElementFields: Array<IDynamicDataSectionConfig> = []): IFormDynamicFieldSection {
    return {
      workflowStep: workflowStep,
      formElements: elements,
      dateOptions: dateOptions,
      nonElementFields: nonElementFields,
      isOpen: elements.length > 0,
      select: this.selectFormElement.bind(this),
    };
  }

  protected defaultFilter(parent: FormElement | Form | CustomGeneratedIdSettings | null): (el: FormElement) => boolean {
    return element => {
      if (parent && 'repeatableAncestors' in parent) {
        return parent.repeatableAncestors.length - element.repeatableAncestors.length <= 1;
      } else {
        return true;
      }
    }
  }

  protected buildFormSectionDateOptions(workflowStep: WorkflowStep): IDynamicDataSectionConfig[] {
    return [
      {
        type: DynamicReferenceType.StepStart,
        modelType: ModelType.DateTime,
        id: `${workflowStep.id}_drt${DynamicReferenceType.StepStart}`,
        label: `${workflowStep.titleOrDefault} Step Start Date`,
        select: this.selectDate.bind(this),
      },
      {
        type: DynamicReferenceType.StepEnd,
        modelType: ModelType.DateTime,
        id: `${workflowStep.id}_drt${DynamicReferenceType.StepEnd}`,
        label: `${workflowStep.titleOrDefault} Step Completion Date`,
        select: this.selectDate.bind(this),
      },
    ];
  }


  selectDate(element) {
    return super.select(element,
      {
        id: element.id,
        modelType: element.modelType,
        label: element.label,
        type: element.type
      })
  }
}